/**
 * ExpiryWatchSection — unified expiry monitoring panel.
 *
 * Aggregates:
 *   - Company documents (trade_license / tax_certificate)
 *   - Employee Emirates IDs (from supervisors)
 *
 * Each row shows: name, reference, expiry date, days-left badge, and a
 * "Send reminder" action that triggers a bilingual SendGrid email.
 *
 * Backend:
 *   GET  /api/admin/expiry-watch?days_threshold=&include_expired=&include_ok=&item_type=
 *   POST /api/admin/expiry-watch/send-reminder  { item_id, to_email?, language?, recipient_name? }
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  AlertTriangle, AlertCircle, Calendar, CheckCircle2, Loader2, Mail, RefreshCw,
  FileText, ShieldCheck, IdCard, Filter, Send, ExternalLink,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/ui/dialog';
import { toast } from 'sonner';
import { getAuthHeaders } from '../../../utils/authHeaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SUBTYPE_META = {
  trade_license:   { ar: 'رخصة تجارية',  en: 'Trade License',     Icon: FileText,    color: '#d4af37' },
  tax_certificate: { ar: 'شهادة ضريبية', en: 'Tax Certificate',   Icon: ShieldCheck, color: '#10B981' },
  emirates_id:     { ar: 'بطاقة الهوية',  en: 'Emirates ID',       Icon: IdCard,      color: '#60A5FA' },
};

const STATUS_BADGE = {
  expired:  { ar: 'منتهية',          en: 'Expired',  cls: 'bg-red-500/15 text-red-400 border-red-500/30' },
  critical: { ar: 'خطر — أقل من 30 يوم', en: '< 30 days', cls: 'bg-orange-500/15 text-orange-400 border-orange-500/30' },
  warning:  { ar: 'تحذير — أقل من 60 يوم', en: '< 60 days', cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  soon:     { ar: 'قريب — أقل من 90 يوم',  en: '< 90 days', cls: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30' },
  ok:       { ar: 'سارية',           en: 'Valid',    cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
};

const FILTER_TABS = [
  { value: 'all',         ar: 'الكل',         en: 'All' },
  { value: 'expired',     ar: 'منتهية',       en: 'Expired' },
  { value: 'critical',    ar: 'خطر',          en: 'Critical' },
  { value: 'warning',     ar: 'تحذير',        en: 'Warning' },
  { value: 'soon',        ar: 'قريب',         en: 'Soon' },
];

const TYPE_TABS = [
  { value: '',             ar: 'الكل',         en: 'All' },
  { value: 'company_doc',  ar: 'وثائق الشركة', en: 'Company docs' },
  { value: 'employee_id',  ar: 'موظفين',       en: 'Employees' },
];

export default function ExpiryWatchSection({ language }) {
  const isAr = language === 'ar';
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ expired: 0, critical: 0, warning: 0, soon: 0, ok: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');     // expired / critical / warning / soon / all
  const [typeFilter, setTypeFilter] = useState('');             // company_doc / employee_id / ''
  const [reminderTarget, setReminderTarget] = useState(null); // selected row
  const [reminderEmail, setReminderEmail] = useState('');
  const [reminderLang, setReminderLang] = useState('ar');
  const [sending, setSending] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        days_threshold: '90',
        include_expired: 'true',
        include_ok: 'false',
      });
      if (typeFilter) params.set('item_type', typeFilter);
      const res = await axios.get(`${BACKEND_URL}/api/admin/expiry-watch?${params}`, { headers: getAuthHeaders() });
      setItems(res.data?.data || []);
      setSummary(res.data?.summary || {});
    } catch (err) {
      toast.error(isAr ? 'فشل تحميل البيانات' : 'Failed to load expiry items');
    } finally {
      setLoading(false);
    }
  }, [typeFilter, isAr]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const filteredItems = items.filter((it) => {
    if (statusFilter === 'all') return true;
    return it.status === statusFilter;
  });

  const openReminder = (row) => {
    setReminderTarget(row);
    setReminderEmail(row.email || '');
    setReminderLang(row.preferred_language || 'ar');
  };

  const sendReminder = async () => {
    if (!reminderTarget) return;
    if (!reminderEmail || !reminderEmail.includes('@')) {
      toast.error(isAr ? 'البريد الإلكتروني غير صالح' : 'Invalid email');
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/expiry-watch/send-reminder`,
        {
          item_id: reminderTarget.id,
          to_email: reminderEmail,
          language: reminderLang,
          recipient_name: reminderTarget.name,
        },
        { headers: getAuthHeaders() },
      );
      if (res.data?.success) {
        toast.success(isAr ? 'تم إرسال التذكير بنجاح' : 'Reminder sent successfully');
        setReminderTarget(null);
      } else {
        toast.error(res.data?.message || (isAr ? 'فشل الإرسال' : 'Send failed'));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isAr ? 'فشل الإرسال' : 'Send failed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
      data-testid="expiry-watch-panel"
    >
      {/* Header */}
      <div className="bg-white/[0.02] backdrop-blur-xl rounded-3xl border border-white/10 p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-400" />
              {isAr ? 'مراقبة الصلاحيات' : 'Expiry Watch'}
            </h2>
            <p className="text-sm text-[#A1A1AA] mt-1">
              {isAr
                ? 'تتبع تواريخ انتهاء وثائق الشركة وبطاقات هوية الموظفين، وأرسل تذكيرات تلقائية.'
                : 'Monitor expiry dates of company documents and employee IDs, send automatic reminders.'}
            </p>
          </div>
          <Button
            onClick={fetchItems}
            disabled={loading}
            data-testid="expiry-watch-refresh"
            className="bg-white/5 hover:bg-white/10 text-white border border-white/10"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ms-2">{isAr ? 'تحديث' : 'Refresh'}</span>
          </Button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="expiry-summary">
          <SummaryCard label={isAr ? 'منتهية' : 'Expired'}   value={summary.expired}  color="#ef4444" Icon={AlertTriangle} testid="sum-expired"/>
          <SummaryCard label={isAr ? '< 30 يوم' : '< 30 d'}   value={summary.critical} color="#fb923c" Icon={AlertCircle}    testid="sum-critical"/>
          <SummaryCard label={isAr ? '< 60 يوم' : '< 60 d'}   value={summary.warning}  color="#f59e0b" Icon={AlertCircle}    testid="sum-warning"/>
          <SummaryCard label={isAr ? '< 90 يوم' : '< 90 d'}   value={summary.soon}     color="#eab308" Icon={Calendar}       testid="sum-soon"/>
          <SummaryCard label={isAr ? 'سارية'   : 'Valid'}    value={summary.ok}       color="#10b981" Icon={CheckCircle2}   testid="sum-ok"/>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 p-4">
        <div className="flex flex-col gap-3">
          {/* status filter */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="status-filter-bar">
            <Filter className="h-4 w-4 text-[#A1A1AA] shrink-0" />
            {FILTER_TABS.map((t) => (
              <button
                key={t.value}
                onClick={() => setStatusFilter(t.value)}
                data-testid={`filter-status-${t.value}`}
                className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                  statusFilter === t.value
                    ? 'bg-[#d4af37] text-black border-[#d4af37] font-semibold'
                    : 'bg-white/5 text-[#A1A1AA] border-white/10 hover:bg-white/10'
                }`}
              >
                {isAr ? t.ar : t.en}
              </button>
            ))}
          </div>

          {/* type filter */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="type-filter-bar">
            <span className="text-xs text-[#71717A]">{isAr ? 'النوع:' : 'Type:'}</span>
            {TYPE_TABS.map((t) => (
              <button
                key={t.value || 'all'}
                onClick={() => setTypeFilter(t.value)}
                data-testid={`filter-type-${t.value || 'all'}`}
                className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                  typeFilter === t.value
                    ? 'bg-white/10 text-white border-white/30'
                    : 'bg-white/5 text-[#A1A1AA] border-white/10 hover:bg-white/10'
                }`}
              >
                {isAr ? t.ar : t.en}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden" data-testid="expiry-table-card">
        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[#d4af37]" />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="p-12 text-center text-[#A1A1AA]" data-testid="expiry-empty-state">
            <CheckCircle2 className="h-10 w-10 mx-auto mb-3 text-emerald-400/70" />
            <p>{isAr ? 'لا توجد عناصر في هذا الفلتر.' : 'No items match this filter.'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="expiry-table">
              <thead className="bg-white/5 text-[#A1A1AA]">
                <tr>
                  <th className="text-start px-4 py-3 font-medium">{isAr ? 'النوع' : 'Type'}</th>
                  <th className="text-start px-4 py-3 font-medium">{isAr ? 'الاسم' : 'Name'}</th>
                  <th className="text-start px-4 py-3 font-medium">{isAr ? 'المرجع' : 'Reference'}</th>
                  <th className="text-start px-4 py-3 font-medium">{isAr ? 'تاريخ الانتهاء' : 'Expiry'}</th>
                  <th className="text-start px-4 py-3 font-medium">{isAr ? 'الحالة' : 'Status'}</th>
                  <th className="text-end   px-4 py-3 font-medium">{isAr ? 'إجراء' : 'Action'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filteredItems.map((row) => {
                  const meta = SUBTYPE_META[row.item_subtype] || SUBTYPE_META.emirates_id;
                  const Icon = meta.Icon;
                  const badge = STATUS_BADGE[row.status] || STATUS_BADGE.ok;
                  return (
                    <tr key={row.id} className="hover:bg-white/[0.02]" data-testid={`expiry-row-${row.id}`}>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-2">
                          <Icon className="h-4 w-4" style={{ color: meta.color }} />
                          <span className="text-white text-xs whitespace-nowrap">{isAr ? meta.ar : meta.en}</span>
                        </span>
                      </td>
                      <td className="px-4 py-3 text-white">{row.name}</td>
                      <td className="px-4 py-3 text-[#A1A1AA] text-xs" dir="ltr">{row.reference || '—'}</td>
                      <td className="px-4 py-3 text-[#d4af37] text-xs" dir="ltr">{row.expiry_date || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] rounded-full border ${badge.cls}`}>
                          {isAr ? badge.ar : badge.en}
                          <span className="opacity-70 ms-1">
                            ({row.days_left < 0
                              ? (isAr ? `منذ ${Math.abs(row.days_left)}ي` : `${Math.abs(row.days_left)}d ago`)
                              : (isAr ? `${row.days_left}ي` : `${row.days_left}d`)})
                          </span>
                        </span>
                      </td>
                      <td className="px-4 py-3 text-end">
                        <Button
                          size="sm"
                          onClick={() => openReminder(row)}
                          data-testid={`send-reminder-${row.id}`}
                          className="bg-[#d4af37]/15 hover:bg-[#d4af37]/25 text-[#d4af37] border border-[#d4af37]/30"
                        >
                          <Mail className="h-3.5 w-3.5" />
                          <span className="ms-1.5 text-xs">{isAr ? 'تذكير' : 'Remind'}</span>
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Send Reminder Modal */}
      <Dialog open={!!reminderTarget} onOpenChange={(o) => !o && setReminderTarget(null)}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 text-white" data-testid="reminder-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Send className="h-5 w-5 text-[#d4af37]" />
              {isAr ? 'إرسال تذكير' : 'Send Reminder'}
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              {reminderTarget?.name} — {reminderTarget?.expiry_date}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs text-[#A1A1AA] mb-1.5 block">{isAr ? 'البريد الإلكتروني للمستلم' : 'Recipient email'}</label>
              <Input
                value={reminderEmail}
                onChange={(e) => setReminderEmail(e.target.value)}
                dir="ltr"
                data-testid="reminder-email-input"
                className="bg-white/5 border-white/10 text-white"
                placeholder="name@example.com"
              />
            </div>

            <div>
              <label className="text-xs text-[#A1A1AA] mb-1.5 block">{isAr ? 'لغة البريد' : 'Email language'}</label>
              <div className="flex gap-2">
                {['ar', 'en'].map((code) => (
                  <button
                    key={code}
                    onClick={() => setReminderLang(code)}
                    data-testid={`reminder-lang-${code}`}
                    className={`flex-1 py-2 rounded-lg text-sm border transition-all ${
                      reminderLang === code
                        ? 'bg-[#d4af37] text-black border-[#d4af37] font-semibold'
                        : 'bg-white/5 text-[#A1A1AA] border-white/10 hover:bg-white/10'
                    }`}
                  >
                    {code === 'ar' ? 'العربية' : 'English'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setReminderTarget(null)}
              disabled={sending}
              data-testid="reminder-modal-cancel"
              className="text-[#A1A1AA]"
            >
              {isAr ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={sendReminder}
              disabled={sending}
              data-testid="reminder-modal-send"
              className="bg-[#d4af37] hover:bg-[#c9a227] text-black font-semibold"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              <span className="ms-2">{isAr ? 'إرسال' : 'Send'}</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}

function SummaryCard({ label, value, color, Icon, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border p-3 flex items-center gap-3"
      style={{ backgroundColor: `${color}10`, borderColor: `${color}30` }}
    >
      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}22` }}>
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] text-[#A1A1AA] truncate">{label}</div>
        <div className="text-lg font-bold text-white">{value ?? 0}</div>
      </div>
    </div>
  );
}
